/**
 * PortionnementQueueTab — Section Stock > Portionnement > File d'attente
 *
 * Affiche les articles reçus en stock qui attendent une confirmation
 * physique du portionnement (ex : "10 kg de viande → 150 portions théoriques,
 * combien obtenues réellement ?").
 *
 * Le magasinier saisit le nombre RÉEL de portions ; un mouvement d'ajustement
 * est automatiquement créé pour tracer l'écart théorique/réel.
 *
 * Backend :
 *   GET  /api/stock/portionnement/queue
 *   POST /api/stock/portionnement/queue/{id}/confirm
 *   POST /api/stock/portionnement/queue/{id}/cancel
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Scale,
  Zap,
  CheckCircle,
  RefreshCw,
  Loader2,
  X,
  Truck,
  Package,
  History,
  Info,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n, decimals = 0) => {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: decimals }).format(n);
};

const PortionnementQueueTab = ({ currentUser }) => {
  const [queue, setQueue] = useState([]);
  const [awaitingCount, setAwaitingCount] = useState(0);
  const [confirmedCount, setConfirmedCount] = useState(0);
  const [awaitingExpected, setAwaitingExpected] = useState(0);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("awaiting"); // 'awaiting' | 'confirmed'
  const [confirmDialog, setConfirmDialog] = useState(null); // entry being confirmed
  const [actualPortions, setActualPortions] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/stock/portionnement/queue`);
      setQueue(r.data?.queue || []);
      setAwaitingCount(r.data?.awaiting_count || 0);
      setConfirmedCount(r.data?.confirmed_count || 0);
      setAwaitingExpected(r.data?.awaiting_expected_portions || 0);
    } catch (e) {
      toast.error("Erreur chargement de la file de portionnement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  // Rafraîchit quand une réception est confirmée ailleurs (peut ajouter des entrées)
  useEffect(() => {
    const h = () => fetchQueue();
    window.addEventListener("stock-reception-confirmed", h);
    return () => window.removeEventListener("stock-reception-confirmed", h);
  }, [fetchQueue]);

  const openConfirm = (entry) => {
    setConfirmDialog(entry);
    setActualPortions(String(entry.expected_portions || ""));
    setNotes("");
  };

  const doConfirm = async () => {
    if (!confirmDialog) return;
    const actual = parseFloat(String(actualPortions).replace(",", "."));
    if (isNaN(actual) || actual < 0) {
      toast.error("Nombre de portions invalide");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API}/stock/portionnement/queue/${confirmDialog.id}/confirm`,
        {
          actual_portions: actual,
          notes,
          confirmed_by: currentUser?.full_name || currentUser?.username || "Magasinier",
          log_adjustment: true,
        }
      );
      const v = r.data?.variance;
      if (v && Math.abs(v) > 0.001) {
        toast.success(`Portionnement confirmé ✅ · Écart : ${v > 0 ? "+" : ""}${v.toFixed(1)} portion(s)`);
      } else {
        toast.success("Portionnement confirmé ✅");
      }
      setConfirmDialog(null);
      fetchQueue();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  const doCancel = async (entry) => {
    if (!window.confirm(`Annuler cette entrée en file ?\n\n${entry.product_name}\n${entry.purchase_qty} ${entry.purchase_unit}`)) return;
    try {
      await axios.post(
        `${API}/stock/portionnement/queue/${entry.id}/cancel`,
        { actor_name: currentUser?.full_name || currentUser?.username || "Utilisateur" }
      );
      toast.success("Entrée annulée");
      fetchQueue();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const filtered = queue.filter((e) => {
    if (tab === "awaiting") return e.status === "awaiting";
    if (tab === "confirmed") return e.status === "confirmed";
    return true;
  });

  return (
    <div className="space-y-3" data-testid="portionnement-queue-tab">
      {/* Header KPIs */}
      <Card className="bg-gradient-to-br from-violet-950/40 to-fuchsia-950/30 border-violet-500/40">
        <CardHeader className="pb-2">
          <CardTitle className="text-violet-200 flex items-center justify-between gap-2 text-base">
            <span className="flex items-center gap-2">
              <Zap className="w-5 h-5" />
              File d&apos;attente Portionnement
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchQueue}
                disabled={loading}
                className="border-violet-500/40 text-violet-200 hover:bg-violet-500/10"
                data-testid="queue-refresh-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-2 border-t border-violet-500/20">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="bg-violet-500/10 border border-violet-500/30 rounded p-2">
              <div className="text-xs text-violet-300">En attente</div>
              <div className="text-2xl font-bold text-violet-100">{awaitingCount}</div>
            </div>
            <div className="bg-fuchsia-500/10 border border-fuchsia-500/30 rounded p-2">
              <div className="text-xs text-fuchsia-300">Portions attendues</div>
              <div className="text-2xl font-bold text-fuchsia-100">{fmt(awaitingExpected)}</div>
            </div>
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-2">
              <div className="text-xs text-emerald-300">Confirmés</div>
              <div className="text-2xl font-bold text-emerald-100">{confirmedCount}</div>
            </div>
          </div>
          <p className="mt-2 text-[11px] text-violet-200/70 flex items-start gap-1.5">
            <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>
              Les achats de produits marqués <strong>mode « queue »</strong> (viande, pain, etc.) attendent ici votre confirmation
              physique du nombre réel de portions obtenues. L&apos;écart théorique/réel est journalisé en mouvement d&apos;ajustement.
            </span>
          </p>
        </CardContent>
      </Card>

      {/* Tabs */}
      <div className="flex gap-1.5">
        {[
          { key: "awaiting", label: "En attente", count: awaitingCount, icon: Zap, color: "violet" },
          { key: "confirmed", label: "Confirmés", count: confirmedCount, icon: CheckCircle, color: "emerald" },
        ].map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              data-testid={`queue-tab-${t.key}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-all ${
                active
                  ? `bg-${t.color}-500/20 text-${t.color}-100 border border-${t.color}-500/50 ring-2 ring-${t.color}-500/30`
                  : "bg-slate-800/50 text-slate-400 border border-slate-700 hover:text-slate-200"
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {t.label}
              <span className="opacity-70 font-mono">({t.count})</span>
            </button>
          );
        })}
      </div>

      {/* Liste */}
      {filtered.length === 0 && !loading ? (
        <Card className="bg-slate-800/30 border-slate-700">
          <CardContent className="py-12 text-center">
            <Scale className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-500 text-base">
              {tab === "awaiting"
                ? "Aucun article en attente de portionnement 🎉"
                : "Aucun portionnement confirmé pour l'instant"}
            </p>
            <p className="text-slate-600 text-xs mt-2">
              {tab === "awaiting"
                ? "Les achats validés de produits en mode « queue » apparaîtront ici."
                : "L'historique des portionnements physiquement validés s'affichera ici."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((e) => {
            const isAwaiting = e.status === "awaiting";
            const variance = e.variance;
            const variancePct = e.variance_pct;
            return (
              <Card
                key={e.id}
                className={`${
                  isAwaiting
                    ? "bg-violet-900/20 border-violet-500/40"
                    : "bg-emerald-900/10 border-emerald-500/30"
                } hover:border-white/30 transition-colors`}
                data-testid={`queue-entry-${e.id}`}
              >
                <CardContent className="p-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Package className={`w-4 h-4 ${isAwaiting ? "text-violet-300" : "text-emerald-300"}`} />
                        <span className="text-white font-semibold truncate max-w-md">
                          {e.product_name}
                        </span>
                        {e.product_code && (
                          <Badge className="text-[9.5px] bg-slate-700/60 text-slate-300 font-mono">
                            {e.product_code}
                          </Badge>
                        )}
                        {e.supplier && (
                          <Badge className="text-[10px] bg-slate-700 text-slate-200">
                            <Truck className="w-3 h-3 mr-0.5" /> {e.supplier}
                          </Badge>
                        )}
                      </div>
                      <div className="mt-1 text-[12px] text-slate-300 flex items-center gap-3 flex-wrap">
                        <span>
                          Reçu : <strong className="text-white">{fmt(e.purchase_qty, 2)} {e.purchase_unit}</strong>
                        </span>
                        <span className="text-slate-500">→</span>
                        <span>
                          Théorique : <strong className="text-violet-300">{fmt(e.expected_portions, 1)} portions</strong>
                          <span className="text-slate-500 ml-1">({fmt(e.portions_per_unit, 1)}/{e.purchase_unit})</span>
                        </span>
                        {!isAwaiting && (
                          <>
                            <span className="text-slate-500">·</span>
                            <span>
                              Réel : <strong className="text-emerald-300">{fmt(e.actual_portions, 1)} portions</strong>
                            </span>
                            {variance !== null && variance !== undefined && (
                              <Badge className={`text-[10px] ${
                                Math.abs(variance) < 0.5
                                  ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"
                                  : variance > 0
                                    ? "bg-cyan-500/20 text-cyan-300 border border-cyan-500/40"
                                    : "bg-rose-500/20 text-rose-300 border border-rose-500/40"
                              }`}>
                                Écart : {variance > 0 ? "+" : ""}{fmt(variance, 1)}
                                {variancePct !== null && variancePct !== undefined ? ` (${variancePct > 0 ? "+" : ""}${variancePct.toFixed(1)}%)` : ""}
                              </Badge>
                            )}
                          </>
                        )}
                      </div>
                      <div className="mt-1 text-[10px] text-slate-500 flex items-center gap-3 flex-wrap">
                        {e.created_at && (
                          <span>Reçu le {(() => { try { return format(parseISO(e.created_at), "dd/MM/yyyy HH:mm", { locale: fr }); } catch { return e.created_at.slice(0,16); } })()}</span>
                        )}
                        {!isAwaiting && e.confirmed_at && (
                          <span>Confirmé le {(() => { try { return format(parseISO(e.confirmed_at), "dd/MM/yyyy HH:mm", { locale: fr }); } catch { return e.confirmed_at.slice(0,16); } })()}</span>
                        )}
                        {e.confirmed_by && !isAwaiting && (
                          <span>par <strong className="text-slate-400">{e.confirmed_by}</strong></span>
                        )}
                        {e.notes && !isAwaiting && (
                          <span className="italic text-slate-400 truncate max-w-md">« {e.notes} »</span>
                        )}
                      </div>
                    </div>
                    {isAwaiting && (
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          onClick={() => openConfirm(e)}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid={`confirm-queue-${e.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" /> Confirmer portions
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => doCancel(e)}
                          className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                          data-testid={`cancel-queue-${e.id}`}
                          title="Annuler cette entrée"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Dialog de confirmation */}
      <Dialog open={!!confirmDialog} onOpenChange={(v) => !v && setConfirmDialog(null)}>
        <DialogContent className="bg-slate-900 border-violet-500/40 max-w-md">
          {confirmDialog && (
            <>
              <DialogHeader>
                <DialogTitle className="text-violet-200 flex items-center gap-2">
                  <Scale className="w-5 h-5" />
                  Confirmer le portionnement
                </DialogTitle>
                <DialogDescription className="text-slate-400">
                  <strong className="text-white">{confirmDialog.product_name}</strong>
                  <br />
                  Reçu : <strong className="text-slate-200">{fmt(confirmDialog.purchase_qty, 2)} {confirmDialog.purchase_unit}</strong>
                  <br />
                  Théorique : <strong className="text-violet-300">{fmt(confirmDialog.expected_portions, 1)} portions</strong>
                  <span className="text-slate-500 text-xs ml-1">
                    ({fmt(confirmDialog.portions_per_unit, 1)}/{confirmDialog.purchase_unit})
                  </span>
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <div>
                  <Label htmlFor="actual" className="text-slate-300 text-xs uppercase font-bold">
                    Portions réellement obtenues
                  </Label>
                  <Input
                    id="actual"
                    type="text"
                    inputMode="decimal"
                    value={actualPortions}
                    onChange={(e) => setActualPortions(e.target.value)}
                    className="bg-slate-800 border-slate-600 text-white text-lg font-mono mt-1"
                    placeholder="Ex: 148"
                    data-testid="queue-actual-input"
                    autoFocus
                  />
                  <p className="text-[10px] text-slate-500 mt-1">
                    <History className="w-3 h-3 inline mr-1" />
                    Théorique {fmt(confirmDialog.expected_portions, 1)}. L&apos;écart sera journalisé.
                  </p>
                </div>
                <div>
                  <Label htmlFor="notes" className="text-slate-300 text-xs uppercase font-bold">
                    Notes (optionnel)
                  </Label>
                  <Textarea
                    id="notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="bg-slate-800 border-slate-600 text-white mt-1"
                    placeholder="Ex: 2 portions perdues par calibrage"
                    rows={2}
                    data-testid="queue-notes-input"
                  />
                </div>
              </div>
              <div className="flex gap-2 pt-1">
                <Button
                  variant="outline"
                  onClick={() => setConfirmDialog(null)}
                  disabled={submitting}
                  className="flex-1 border-slate-600 text-slate-300"
                  data-testid="queue-confirm-cancel"
                >
                  Annuler
                </Button>
                <Button
                  onClick={doConfirm}
                  disabled={submitting || !actualPortions}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="queue-confirm-submit"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Enregistrement…</>
                  ) : (
                    <><CheckCircle className="w-4 h-4 mr-1" /> Valider</>
                  )}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PortionnementQueueTab;
